import { isDecorative } from "./decorative-segments.ts";

export type Pick =
  | { kind: "point"; index: number }
  | { kind: "line"; index: number }
  | { kind: "empty" };

export interface GraphSelection {
  points: number[];
  lines: number[];
}

/**
 * Every point, with the line list cleared. A line whose two ends are both
 * selected lights up the same way a lasso that contains those points does.
 */
export function selectEverything(pointCount: number): GraphSelection {
  const points = new Array<number>(pointCount);
  for (let i = 0; i < pointCount; i++) points[i] = i;
  return { points, lines: [] };
}

/** True when `selection` is exactly `0 .. pointCount-1` in order. */
export function isFullPointSelection(selection: readonly number[], pointCount: number): boolean {
  if (selection.length !== pointCount) return false;
  for (let i = 0; i < pointCount; i++) {
    if (selection[i] !== i) return false;
  }
  return true;
}

/** True when `lines` is exactly `0 .. lineCount-1` in order. */
export function isFullLineSelection(lines: readonly number[], lineCount: number): boolean {
  if (lines.length !== lineCount) return false;
  for (let i = 0; i < lineCount; i++) {
    if (lines[i] !== i) return false;
  }
  return true;
}

/**
 * Line-highlight override.
 * "points" keeps every line off, even when both ends are selected.
 * "lines" paints every line without listing them.
 */
export type SelectAllStep = "all" | "points" | "lines";

/** A selects every visible point, or only lines when points are hidden. */
export function selectAllMatching(pointsVisible: boolean, pointCount: number, lineCount: number): GraphSelection {
  if (!pointsVisible) return { points: [], lines: indexRange(lineCount) };
  return selectEverything(pointCount);
}

/**
 * True when every visible item is already selected.
 * Hidden points do not count. A line counts when it is listed or both ends are selected.
 */
export function selectionCoversVisible(
  pointsVisible: boolean,
  current: GraphSelection,
  pointCount: number,
  segments: ReadonlyArray<readonly [number, number]>,
): boolean {
  if (pointsVisible && !coversIndexes(current.points, pointCount)) return false;
  return coversLines(current, segments);
}

export type SelectAllCycleToast = "all" | "excludeDecorative" | "clear";

/** True when at least one segment is marked decorative (장식선). */
export function hasDecorativeSegments(decorative: readonly boolean[] | undefined, lineCount: number): boolean {
  for (let i = 0; i < lineCount; i++) {
    if (isDecorative(decorative, i)) return true;
  }
  return false;
}

/** Every non-decorative segment, without selecting decorative lines implicitly via points. */
export function selectAllExcludingDecorative(
  pointsVisible: boolean,
  lineCount: number,
  decorative: readonly boolean[] | undefined,
): GraphSelection {
  void pointsVisible;
  const lines: number[] = [];
  for (let i = 0; i < lineCount; i++) {
    if (!isDecorative(decorative, i)) lines.push(i);
  }
  return { points: [], lines };
}

/**
 * Repeated A cycles: all segments → non-decorative segments → clear → …
 * `lastPhase` is the step last applied by this shortcut; 0 restarts at all.
 * When there are no decorative segments, the middle step is skipped.
 */
export function cycleSelectAll(
  pointsVisible: boolean,
  pointCount: number,
  segments: ReadonlyArray<readonly [number, number]>,
  decorative: readonly boolean[] | undefined,
  lastPhase: 0 | 1 | 2 | 3,
): { next: GraphSelection; phase: 1 | 2 | 3; toast: SelectAllCycleToast } {
  const lineCount = segments.length;
  const hasDecor = hasDecorativeSegments(decorative, lineCount);
  const empty = { points: [] as number[], lines: [] as number[] };
  const all = selectAllMatching(pointsVisible, pointCount, lineCount);

  if (lastPhase === 0 || lastPhase === 3) {
    return { next: all, phase: 1, toast: "all" };
  }
  if (lastPhase === 1) {
    if (hasDecor) {
      return {
        next: selectAllExcludingDecorative(pointsVisible, lineCount, decorative),
        phase: 2,
        toast: "excludeDecorative",
      };
    }
    return { next: empty, phase: 3, toast: "clear" };
  }
  return { next: empty, phase: 3, toast: "clear" };
}

/** Select everything visible, or clear when that set is already selected. */
export function toggleSelectAll(
  pointsVisible: boolean,
  current: GraphSelection,
  pointCount: number,
  segments: ReadonlyArray<readonly [number, number]>,
): GraphSelection {
  if (selectionCoversVisible(pointsVisible, current, pointCount, segments)) return { points: [], lines: [] };
  return selectAllMatching(pointsVisible, pointCount, segments.length);
}

/** A hidden point is not a click target. A line under that point still is. */
export function selectableHit(pointsVisible: boolean, point: number | null, line: number | null): Pick {
  if (pointsVisible && point != null) return { kind: "point", index: point };
  if (line != null) return { kind: "line", index: line };
  return { kind: "empty" };
}

function coversIndexes(indexes: readonly number[], count: number): boolean {
  if (count === 0) return true;
  if (indexes.length < count) return false;
  const seen = new Set<number>();
  for (const index of indexes) {
    if (index >= 0 && index < count) seen.add(index);
  }
  return seen.size === count;
}

function coversLines(current: GraphSelection, segments: ReadonlyArray<readonly [number, number]>): boolean {
  if (segments.length === 0) return true;
  const listed = new Set(current.lines);
  const points = new Set(current.points);
  for (let i = 0; i < segments.length; i++) {
    if (listed.has(i)) continue;
    const ends = segments[i];
    if (ends && points.has(ends[0]) && points.has(ends[1])) continue;
    return false;
  }
  return true;
}

/**
 * Draft-scene flag for visibility.
 * A complete line selection with points hidden paints in one pass.
 */
export function lineHighlightFor(
  pointsVisible: boolean,
  points: readonly number[],
  lines: readonly number[],
  lineCount: number,
): SelectAllStep | null {
  if (!pointsVisible && points.length === 0 && isFullLineSelection(lines, lineCount)) return "lines";
  return null;
}

function indexRange(count: number): number[] {
  const indexes = new Array<number>(count);
  for (let i = 0; i < count; i++) indexes[i] = i;
  return indexes;
}

/**
 * Desktop click. Shift toggles the hit and leaves the other list.
 * A plain click on something else keeps just that item. A plain click on the
 * only selected point or line clears the selection.
 */
export function applyClick(current: GraphSelection, hit: Pick, shift: boolean): GraphSelection {
  if (hit.kind === "empty") {
    return shift ? { points: current.points.slice(), lines: current.lines.slice() } : { points: [], lines: [] };
  }
  if (shift) {
    if (hit.kind === "point") {
      return {
        points: toggleIndex(current.points, hit.index),
        lines: current.lines.slice(),
      };
    }
    return {
      points: current.points.slice(),
      lines: toggleIndex(current.lines, hit.index),
    };
  }
  if (hit.kind === "point") {
    if (solePoint(current, hit.index)) return { points: [], lines: [] };
    return { points: [hit.index], lines: [] };
  }
  if (soleLine(current, hit.index)) return { points: [], lines: [] };
  return { points: [], lines: [hit.index] };
}

function solePoint(current: GraphSelection, index: number): boolean {
  return current.lines.length === 0 && current.points.length === 1 && current.points[0] === index;
}

function soleLine(current: GraphSelection, index: number): boolean {
  return current.points.length === 0 && current.lines.length === 1 && current.lines[0] === index;
}

function toggleIndex(current: readonly number[], index: number): number[] {
  if (current.includes(index)) return current.filter((item) => item !== index);
  return [...current, index].sort((a, b) => a - b);
}
